import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  SLEEVE_BUDGET,
  INVESTOR_PROFILES,
  computeAggressiveness,
  sumEffect,
  findEffectValue,
} from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "wildcat-portfolio";

function formatMoney(n) {
  const sign = n < 0 ? "-" : "";
  return sign + Math.abs(Math.round(n)).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function computeOutcome(reconResult, plan, campaignResult) {
  const { allocations, profileId } = plan;
  const { log } = campaignResult;

  const realEstateReturn =
    (allocations.realestate || 0) * 0.08 + sumEffect(log, "realEstateBonus") - sumEffect(log, "realEstatePenalty");

  const goldReturn = (allocations.gold || 0) * 0.05 + sumEffect(log, "goldBonus") - sumEffect(log, "goldPenalty");

  const cryptoOutcome = findEffectValue(log, "cryptoOutcome");
  const cryptoReturn = (allocations.crypto || 0) * (cryptoOutcome === "hold" ? 0.15 : -0.4);

  const collectiblesOutcome = findEffectValue(log, "collectiblesOutcome");
  const collectiblesReturn = (allocations.collectibles || 0) * (collectiblesOutcome === "patient" ? 0.1 : -0.3);

  const businessOutcome = findEffectValue(log, "businessOutcome");
  const businessReturn = (allocations.business || 0) * 0.06 + (businessOutcome === "invest" ? 400 : -100);

  const goodDebtBonus = sumEffect(log, "goodDebtBonus");

  const totalReturn = realEstateReturn + goldReturn + cryptoReturn + collectiblesReturn + businessReturn + goodDebtBonus;
  const finalValue = SLEEVE_BUDGET + totalReturn;

  const profile = INVESTOR_PROFILES.find((p) => p.id === profileId);
  const aggressiveness = computeAggressiveness(allocations);
  const [lo, hi] = profile.aggressivenessRange;
  const fitsProfile = aggressiveness >= lo && aggressiveness <= hi;
  const fitBonus = fitsProfile ? 100 : 0;

  const goodChoices = log.filter((l) => l.choice.tag === "good").length;
  const badChoices = log.filter((l) => l.choice.tag === "bad").length;

  const reconScore = reconResult.correctCount * 15;
  const choiceScore = goodChoices * 20 - badChoices * 20;
  const returnBonus = Math.max(-100, Math.min(300, Math.round(totalReturn / 10)));
  const score = Math.max(50, reconScore + choiceScore + returnBonus + fitBonus + 50);

  return {
    realEstateReturn,
    goldReturn,
    cryptoReturn,
    collectiblesReturn,
    businessReturn,
    goodDebtBonus,
    totalReturn,
    finalValue,
    profile,
    aggressiveness,
    fitsProfile,
    goodChoices,
    badChoices,
    score,
  };
}

export default function DebriefStage({ reconResult, plan, campaignResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(reconResult, plan, campaignResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Wildcat Portfolio results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">EXPEDITION DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">{outcome.profile.label}</p>

      <div className="mx-auto mb-4 max-w-sm border-2 border-gold bg-gold/10 p-5">
        <p className="label-pixel mb-1 text-gold">Total Return (1 Year)</p>
        <p className={`font-display text-2xl ${outcome.totalReturn >= 0 ? "text-gold" : "text-danger"}`}>
          {formatMoney(outcome.totalReturn)}
        </p>
        <p className="mt-2 text-[11px] text-mist/60">
          On a {formatMoney(SLEEVE_BUDGET)} sleeve, ending at {formatMoney(outcome.finalValue)}.
        </p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Recon Accuracy</span>
          <span className="text-cyan">{reconResult.correctCount}/5</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{outcome.goodChoices}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{outcome.badChoices}</span>
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Fits Your Profile?</span>
          <span className={outcome.fitsProfile ? "text-lime" : "text-gold"}>{outcome.fitsProfile ? "Yes" : "Worth revisiting"}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Real Estate</span>
          <span>{formatMoney(outcome.realEstateReturn)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Gold & Silver</span>
          <span>{formatMoney(outcome.goldReturn)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Crypto</span>
          <span>{formatMoney(outcome.cryptoReturn)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Collectibles</span>
          <span>{formatMoney(outcome.collectiblesReturn)}</span>
        </div>
        <div className="flex justify-between text-[11px] text-mist/50">
          <span>Business & Skills</span>
          <span>{formatMoney(outcome.businessReturn)}</span>
        </div>
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mx-auto mb-6 max-w-sm text-[11px] text-mist/50">
        Notice the prerequisites gate never showed up as a number here — it's the reason this
        whole sleeve could safely exist as "extra," not the foundation.
      </p>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ RUN A NEW EXPEDITION
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
